
import React from 'react';
import { AlertCircle } from 'lucide-react';
import InterviewMedia from '../InterviewMedia';
import { Interview } from '@/types/interview';

export interface InterviewDialogContentProps {
  interview: Interview;
  error: string | null;
  dialogKey: number;
  photoUrl: string | undefined;
}

const InterviewDialogContent: React.FC<InterviewDialogContentProps> = ({
  interview,
  error,
  dialogKey,
  photoUrl
}) => {
  const effectivePhotoUrl = photoUrl ?? interview.photoUrl;
  return (
    <div className="space-y-4" key={dialogKey}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      <div>
        <h3 className="font-medium">Yrkestittel</h3>
        <p>{interview.jobTitle}</p>
      </div>
      
      <div className="flex space-x-4">
        <div className="flex-1">
          <h3 className="font-medium">Intervjuobjekt</h3>
          <p>{interview.intervieweeName}</p>
          {interview.company && (
            <p className="text-sm text-gray-500">{interview.company}</p>
          )}
        </div>
        
        <div className="flex-shrink-0">
          <div>
            {effectivePhotoUrl ? (
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {effectivePhotoUrl.length > 30 
                    ? `${effectivePhotoUrl.substring(0, 30)}...` 
                    : effectivePhotoUrl}
                </p>
                <InterviewMedia 
                  intervieweeName={interview.intervieweeName} 
                  photoUrl={effectivePhotoUrl}
                  className="w-24 h-24"
                />
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 mb-1">No photo available</p>
                <InterviewMedia 
                  intervieweeName={interview.intervieweeName} 
                  className="w-24 h-24"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div>
        <h3 className="font-medium">Kontekst</h3>
        <p className="whitespace-pre-wrap">{interview.context}</p>
      </div>
      
      {interview.contextUrl && (
        <div>
          <h3 className="font-medium">Kontekst URL</h3>
          <a 
            href={interview.contextUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            {interview.contextUrl}
          </a>
        </div>
      )}
      
      <div>
        <h3 className="font-medium">Spørsmål</h3>
        <p className="whitespace-pre-wrap">{interview.question}</p>
      </div>
      
      <div>
        <h3 className="font-medium">Svar</h3>
        <p className="whitespace-pre-wrap">{interview.answer}</p>
      </div>
      
      <div>
        <h3 className="font-medium">Emneord</h3>
        <div className="flex flex-wrap gap-1 mt-1">
          {interview.tags && interview.tags.length > 0 ? (
            interview.tags
              .filter(tag => typeof tag === 'string' && tag.trim() !== '' && tag !== '{}' && tag !== '"#{}"')
              .map((tag, index) => {
                // Enhanced tag cleaning
                let cleanTag = String(tag)
                  .replace(/^["']|["']$/g, '') // Remove surrounding quotes
                  .replace(/["']/g, '') // Remove ANY quotes within the string
                  .replace(/^#+/, '') // Remove any leading hashtags
                  .replace(/[{}]/g, '') // Remove curly braces
                  .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                  .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
                  .trim();
                
                return cleanTag ? (
                  <span key={`${cleanTag}-${index}`} className="bg-gray-100 px-2 py-1 rounded text-sm">
                    #{cleanTag}
                  </span>
                ) : null;
              })
              .filter(Boolean) // Remove null entries
          ) : (
            <span className="text-gray-500">Ingen emneord</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewDialogContent;
